import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import FormButton from '@ui/button/FormButton';
import FormCheckbox from '@ui/checkbox/FormCheckbox';
import useWebStorage from '@app/component/web-storage/useWebStorage';
import useSiteState from '@app/state/SiteState';
import {
	DirectSubmitNoticeRequest,
	directSubmitNoticeTitle,
	dismissDirectSubmitNotice,
	isDirectSubmitNoticeDismissed,
	subscribeDirectSubmitNotice,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/DirectSubmitNotice.utilities';

// The first-push notice (a Screen Part of the date-based table): a confirm popover under the plane, and
// inside the percent editor after Enter. "Send N rates now?", the date, the checkbox "Do not show this
// again", Cancel and Submit. The notice comes back on each push until the box is ticked and Submit is
// clicked. The choice lives in the browser, per user. Built from the Popover of headlessui (the pencil
// editor uses the same), FormCheckbox and FormButton (small); no new Component.

// Whether the notice is still needed, shared by every row of the table (a Submit in one row closes it for all).
export const useDirectSubmitNoticeChoice = () => {
	const storage = useWebStorage();
	const principal = useSiteState(state => state.principal);
	const userId = principal?.id;
	const needed = useSyncExternalStore(subscribeDirectSubmitNotice, () => !isDirectSubmitNoticeDismissed(storage, userId), () => true);
	const dismiss = useCallback(() => dismissDirectSubmitNotice(storage, userId), [storage, userId]);
	return { needed, dismiss };
};

export const DirectSubmitNoticePanel: React.FunctionComponent<{
	request: DirectSubmitNoticeRequest,
	contextKey: string,
	onCancel: () => void,
	onSubmit: (expectedContext: string) => boolean,
}> = ({ request, contextKey, onCancel, onSubmit }) => {
	const { dismiss } = useDirectSubmitNoticeChoice();
	const [ticked, setTicked] = useState(false);
	const [openedContext] = useState(contextKey);
	const sent = useRef(false);
	useEffect(() => { if (openedContext !== contextKey) onCancel(); }, [openedContext, contextKey, onCancel]);
	if (openedContext !== contextKey) return null;
	const submit = () => {
		if (sent.current) return;
		if (onSubmit(openedContext)) {
			sent.current = true;
			if (ticked) dismiss();
		}
		onCancel();
	};
	return <div role="dialog" aria-label="First-push notice" className="w-72 p-3 text-left">
		<p className="text-sm font-semibold text-gray-900">{directSubmitNoticeTitle(request.rateCount)}</p>
		<p className="mt-1 text-xs leading-5 text-gray-600">
			<span className="font-semibold text-gray-900">{request.dateLabel}</span> goes to your booking engine at once. No review step.
		</p>
		{request.detail !== '' && <p className="mt-0.5 text-xs text-gray-500">{request.detail}</p>}
		<div className="mt-3">
			<FormCheckbox name="dismissDirectSubmitNotice" label="Do not show this again" labelExtraClassName="text-xs"
										checked={ticked} onChange={event => setTicked(event.target.checked)} />
		</div>
		<div className="mt-3 flex justify-end gap-x-1.5">
			<FormButton size="small" state="white" onClick={onCancel}>Cancel</FormButton>
			<FormButton size="small" state="primary" onClick={submit}>Submit</FormButton>
		</div>
	</div>;
};

export const NOTICE_PANEL_CLASS = 'z-50 mt-2 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5';
